import { createContext, useContext, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { getHome, addHome, updateHome } from "../../api/home";
const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const clientListArray = useRef();
  const ourPartnershipImageArray = useRef();
  const industriesListArray = useRef();
  const technologyListArray = useRef();
  const creativesListArray = useRef();
  const spotilightListArray = useRef();
  const toaster = useToaster();

  const getHomeQuery = useQuery({
    queryKey: ["home"],
    queryFn: getHome,
  });
  const addHomeQuery = useMutation({ mutationFn: addHome });
  const updateHomeQuery = useMutation({ mutationFn: updateHome });

  const initialValues = useMemo(() => {
    const homeData = getHomeQuery?.data?.data?.[0] || {};

    return {
      meta: {
        meta_title: homeData?.meta_title || "",
        meta_slug_url: homeData?.meta_slug_url || "",
        meta_description: homeData?.meta_description || "",
        keywords: homeData?.keywords || "",
      },
      banner: {
        heading: homeData?.heading || "",
        sub_heading: homeData?.sub_heading || "",
        cta_label: homeData?.cta_label || "",
        cta_label_redirect_link: homeData?.cta_label_redirect_link || "",
        description: homeData?.description || "",
      },
      creative: {
        heading: homeData?.creatives?.heading || "",
        sub_heading: homeData?.creatives?.sub_heading || "",
        creativeList: homeData?.creatives?.creatives?.map((item) => ({
          title: item.title || "",
          description: item.description || "",
          image: item.image || "",
          redirect_link: item.redirect_link || "",
        })),
      },

      client: {
        client_heading: homeData?.client_heading || "",
        clientList: homeData?.clients?.map((item) => ({
          image_url: item.image_url || "",
        })),
      },

      our_partnership: {
        heading: homeData?.partnerships?.heading || "",

        our_partnership_image: homeData?.partnerships?.partnerships?.map(
          (item) => ({
            image_link: item.image_link || "",
            cta_link: item.cta_link || "",
          })
        ),
      },
      industries: {
        heading: homeData?.industries?.heading || "",
        industriesList: homeData?.industries?.industries?.map((item) => ({
          title: item.title || "",
        })),
      },
      spotlight_details: {
        heading: homeData?.spotlight_details?.heading || "",
        sub_heading: homeData?.spotlight_details?.sub_heading || "",
        spotlights: homeData?.spotlight_details?.spotlights?.map((item) => ({
          title: item.title || "",
          cta: item.cta || "",
          logo: item.logo || "",
        })),
      },
      technology: {
        heading: homeData?.technology?.heading || "",
      },
      technologyList: homeData?.technology?.technologies?.map((item) => ({
        title: item.title || "",
      })),
      brochure_download: {
        title: homeData?.brochure_download?.title || "",
        file: homeData?.brochure_download?.file || "",
      },
      book_a_demo: {
        title: homeData?.book_a_demo?.title || "",
        link: homeData?.book_a_demo?.link || "",
      },
      testimonial_heading: homeData?.testimonial_heading || "",
      works_heading: homeData?.works_heading || "",
      insights_heading: homeData?.insights_heading || "",
    };
  }, [getHomeQuery]);

  const handleSubmit = async (values) => {

    const payload = {
      meta_title: values?.meta?.meta_title || "",
      meta_slug_url: values?.meta?.meta_slug_url || "",
      meta_description: values?.meta?.meta_description || "",
      keywords: values?.meta?.keywords || "",

      heading: values?.banner?.heading || "",
      sub_heading: values?.banner?.sub_heading || "",
      cta_label: values?.banner?.cta_label || "",
      cta_label_redirect_link: values?.banner?.cta_label_redirect_link || "",
      description: values?.banner?.description || "",

      creatives: {
        heading: values?.creative?.heading || "",
        sub_heading: values?.creative?.sub_heading || "",
        description: values?.creative?.description || "",

        creatives: values?.creative?.creativeList?.map((item) => ({
          title: item.title || "",
          description: item.description || "",
          image: item.image || "",
          redirect_link: item.redirect_link || "",
        })),
      },

      client_heading: values?.client?.client_heading || "",
      clients: values?.client?.clientList?.map((item) => ({
        image_url: item.image_url || "",
      })),
      partnerships: {
        heading: values?.our_partnership?.heading || "",
        partnerships: values?.our_partnership?.our_partnership_image?.map(
          (item) => ({
            image_link: item.image_link || "",
            cta_link: item.cta_link || "",
          })
        ),
      },

      industries: {
        heading: values?.industries?.heading || "",
        industries: values?.industries?.industriesList?.map((item) => ({
          title: item.title || "",
        })),
      },
      spotlight_details: {
        heading: values?.spotlight_details?.heading || "",
        sub_heading: values?.spotlight_details?.sub_heading || "",
        spotlights: values?.spotlight_details?.spotlights?.map((item) => ({
          title: item.title || "",
          cta: item.cta || "",
          logo: item.logo || "",
        })),
      },

      technology: {
        heading: values?.technology?.heading || "",
        technologies: values?.technologyList?.map((item) => ({
          title: item.title || "",
        })),
      },
      brochure_download: {
        title: values?.brochure_download?.title || "",
        file: values?.brochure_download?.file || "",
      },
      book_a_demo: {
        title: values?.book_a_demo?.title || "",
        link: values?.book_a_demo?.link || "",
      },

      testimonial_heading: values?.testimonial_heading || "",
      works_heading: values?.works_heading || "",
      insights_heading: values?.insights_heading || "",
    };
    const homeData = getHomeQuery?.data?.data?.[0] || {};

    if (homeData._id) {
      payload.id = homeData._id;
      updateHomeQuery.mutate(payload, {
        onSuccess: () => {
          toaster.success(
            Const.MESSAGES.SUCCESS,
            Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY
          );
        },
      });
      return;
    }
    addHomeQuery.mutate(payload, {
      onSuccess: () => {
        toaster.success(
          Const.MESSAGES.SUCCESS,
          Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY
        );
      },
    });
  };

  const handleSpotlightAdd = () => {
    spotilightListArray.current.push({ title: "", image: "", cta: "" });
  };

  const handleSpotlightDelete = (index) => () => {
    spotilightListArray.current.remove(index);
  };

  const handleClientAdd = () => {
    clientListArray.current.push({ title: "", image: "" });
  };

  const handleClientDelete = (index) => () => {
    clientListArray.current.remove(index);
  };

  const handleCreativesAdd = () => {
    creativesListArray.current.push({ heading: "", description: "", image: "", redirect_link: "" });
  };

  const handleCreativesDelete = (index) => () => {
    creativesListArray.current.remove(index);
  };

  const handlePartnershipImageAdd = () => {
    ourPartnershipImageArray.current.push({ banner_image: "" });
  };

  const handlePartnershipImageDelete = (index) => () => {
    ourPartnershipImageArray.current.remove(index);
  };

  const handleIndustriesAdd = () => {
    industriesListArray.current.push({ title: "", image: "" });
  };

  const handleIndustriesDelete = (index) => () => {
    industriesListArray.current.remove(index);
  };

  const handleTechnologyAdd = () => {
    technologyListArray.current.push({ title: "", image: "" });
  };

  const handleTechnologyDelete = (index) => () => {
    technologyListArray.current.remove(index);
  };

  return (
    <PageContext.Provider
      value={{
        initialValues,
        handleClientAdd,
        clientListArray,
        handleClientDelete,
        handlePartnershipImageAdd,
        ourPartnershipImageArray,
        handlePartnershipImageDelete,
        industriesListArray,
        handleIndustriesAdd,
        handleTechnologyAdd,
        technologyListArray,
        handleIndustriesDelete,
        handleTechnologyDelete,
        handleSubmit,
        getHomeQuery,
        handleCreativesAdd,
        handleCreativesDelete,
        creativesListArray,
        handleSpotlightAdd,
        handleSpotlightDelete,
        spotilightListArray,
      }}>
      {children}
    </PageContext.Provider>
  );
};
export const usePageProvider = () => {
  return useContext(PageContext);
};
